package com.kinoshazam.screens.login.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.screens.login.expanded.ExpandedLoginContent
import com.kinoshazam.uiKit.button.ButtonContentState
import com.kinoshazam.uiKit.button.UiKitButton
import com.kinoshazam.uiKit.button.UiKitButtonState
import com.kinoshazam.uiKit.components.textField.compact.CompactTextField
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.extension.uiKitImePadding
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.Small
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.TextFieldPreset
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.change_password_question
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.login_button_title
import kinoshazam.resources.generated.resources.login_email_verification_error_message
import kinoshazam.resources.generated.resources.login_error
import kinoshazam.resources.generated.resources.login_error_message
import kinoshazam.resources.generated.resources.login_hint
import kinoshazam.resources.generated.resources.login_label
import kinoshazam.resources.generated.resources.login_title
import kinoshazam.resources.generated.resources.login_unknown_error_message
import kinoshazam.resources.generated.resources.password_error
import kinoshazam.resources.generated.resources.password_hint
import kinoshazam.resources.generated.resources.password_label
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import presentation.screens.login.LoginMVIAction
import presentation.screens.login.LoginState
import presentation.screens.login.LoginViewModel
import utils.FormFactor

@Composable
fun CompactLoginContentHost(
    formFactor: FormFactor = FormFactor.Compact,
) {
    val viewModel = koinViewModel<LoginViewModel>()
    val loginState by viewModel.state.collectAsStateWithLifecycle()

    when (formFactor) {
        FormFactor.Expanded -> {
            Scaffold(
                modifier = Modifier
                    .fillMaxSize()
                    .background(UiKitTheme.colors.transparent),
                containerColor = UiKitTheme.colors.transparent,
            ) {
                DialogContent(
                    onClose = { viewModel.onAction(LoginMVIAction.BackPressed) },
                ) {
                    CompactLoginContent(
                        state = loginState,
                        onAction = viewModel::onAction,
                        asDialog = true,
                    )
                }
            }
        }

        FormFactor.ExtraLarge -> ExpandedLoginContent(
            state = loginState,
            onAction = viewModel::onAction,
        )

        else -> CompactLoginContent(
            state = loginState,
            onAction = viewModel::onAction,
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun CompactLoginContent(
    state: LoginState,
    modifier: Modifier = Modifier,
    asDialog: Boolean = false,
    onAction: (LoginMVIAction) -> Unit,
) {

    val snackbarHostState = remember { SnackbarHostState() }
    val authError = stringResource(Res.string.login_error_message)
    val verificationError = stringResource(Res.string.login_email_verification_error_message)
    val unknownError = stringResource(Res.string.login_unknown_error_message)

    LaunchedEffect(state) {
        state.error?.let {
            snackbarHostState.showSnackbar(
                when (it) {
                    LoginState.ErrorType.Credential -> authError
                    LoginState.ErrorType.Verification -> verificationError
                    LoginState.ErrorType.Unknown -> unknownError
                }
            )
            onAction(LoginMVIAction.ErrorDismissed)
        }
    }

    Scaffold(
        modifier = modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .background(UiKitTheme.colors.color_3),
        containerColor = UiKitTheme.colors.color_3,
        topBar = {
            TopAppBar(
                title = {
                    TextLabel(
                        state = TextLabelState(
                            text = stringResource(Res.string.login_title),
                            color = UiKitTheme.colors.color_2,
                            textStyle = BoldLarge
                        )
                    )
                },
                navigationIcon = {
                    if (!asDialog) {
                        Image(
                            painter = painterResource(Res.drawable.left),
                            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                            contentDescription = null,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                                .clickable { onAction(LoginMVIAction.BackPressed) }
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = UiKitTheme.colors.color_3,
                ),
            )
        },
        snackbarHost = {
            SnackbarHost(
                modifier = Modifier.navigationBarsPadding().uiKitImePadding(),
                hostState = snackbarHostState,
            )
        },
        content = { paddingValues ->
            ContentScreen(paddingValues, state, onAction, asDialog = asDialog)
        }
    )
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    state: LoginState,
    onAction: (LoginMVIAction) -> Unit,
    asDialog: Boolean = false,
) {

    Column(
        modifier = Modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .verticalScroll(rememberScrollState())
            .padding(paddingValues)
            .uiKitImePadding(),
    ) {
        if (!asDialog) {
            Spacer(modifier = Modifier.weight(1f))
        }

        CompactTextField(
            modifier = Modifier.padding(16.dp),
            state = TextFieldPreset.emailState(
                text = state.email,
                hint = TextLabelState(
                    text = stringResource(Res.string.login_label),
                    color = UiKitTheme.colors.color_6,
                ),
                placeholder = TextLabelState(
                    text = stringResource(Res.string.login_hint),
                    color = UiKitTheme.colors.color_6,
                ),
                error = TextLabelState(
                    text = stringResource(Res.string.login_error),
                    color = UiKitTheme.colors.error,
                    textStyle = Small,
                ).takeIf { state.isLoginError },
            ),
            onValueChanged = {
                onAction(LoginMVIAction.EmailChanged(it))
            }
        )

        CompactTextField(
            modifier = Modifier.padding(16.dp),
            state = TextFieldPreset.passwordState(
                text = state.password,
                hint = TextLabelState(
                    text = stringResource(Res.string.password_label),
                    color = UiKitTheme.colors.color_6,
                ),
                placeholder = TextLabelState(
                    text = stringResource(Res.string.password_hint),
                    color = UiKitTheme.colors.color_6
                ),
                error = TextLabelState(
                    text = stringResource(Res.string.password_error),
                    color = UiKitTheme.colors.error
                ).takeIf { state.isPasswordError },
                passwordVisible = state.passwordVisible,
            ),
            onRightIconClick = {
                onAction(LoginMVIAction.HidePasswordClicked)
            },
            onValueChanged = {
                onAction(LoginMVIAction.PasswordChanged(it))
            }
        )

        TextLabel(
            state = TextLabelState(
                text = stringResource(Res.string.change_password_question),
                color = UiKitTheme.colors.color_1,
                textStyle = Medium
            ),
            modifier = Modifier
                .padding(16.dp)
                .clickable {
                    onAction(LoginMVIAction.ForgotPasswordClicked)
                }
        )

        if (!asDialog) {
            Spacer(modifier = Modifier.weight(1f))
        } else {
            Spacer(modifier = Modifier.height(24.dp))
        }

        UiKitButton(
            state = UiKitButtonState(
                title = TextLabelState(
                    text = stringResource(Res.string.login_button_title),
                    textStyle = BoldMedium,
                    color = UiKitTheme.colors.color_7,
                ),
                contentState = if (state.isLoading) ButtonContentState.LOADING else ButtonContentState.ENABLED,
                backgroundColor = UiKitTheme.colors.color_1
            ),
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .height(48.dp),
            onClick = {
                onAction(LoginMVIAction.LoginClicked)
            }
        )
    }
}
